import type {
  AdminAnnouncementRecord,
  AdminGateway,
  AdminInvitationOutcome,
  AdminInvitationRecord,
  AdminPermissionGroup,
  AdminRoleRecord,
  AdminSnapshot,
  AdminUserRecord,
  DecideAccessRequestInput,
  InviteAdminUserInput,
  SaveAnnouncementInput,
  SystemAnnouncement,
} from './types';

const roleTemplates = [
  ['administrator', 'Administrator', 'Vollzugriff auf Mandant und FORQ Studio'],
  ['management', 'Geschäftsführung', 'Auswertungen, Freigaben und Audit'],
  ['operations', 'Betriebsleitung', 'Operative Leitung und Freigaben'],
  ['administration', 'Verwaltung', 'Stammdaten und kaufmännische Bearbeitung'],
  ['service', 'Serviceleitung', 'Service-, Werkstatt- und Einsatzsteuerung'],
  ['workshop', 'Werkstatt', 'Werkstattaufträge und Maschinenbearbeitung'],
  ['technician', 'Monteur', 'Eigene Aufträge und Rückmeldungen'],
  ['sales', 'Vertrieb', 'Kunden, Maschinen und Verkauf'],
  ['warehouse', 'Lager', 'Artikel, Bestände und Beschaffung'],
  ['logistics-management', 'Speditionsleitung', 'Transportplanung und Freigaben'],
  ['logistics', 'Spedition', 'Eigene Transportaufträge'],
] as const;

const permissionGroups: readonly AdminPermissionGroup[] = [
  {
    moduleId: 'platform',
    label: 'Plattform',
    permissions: [
      { key: 'platform.access', moduleId: 'platform', label: 'FORQ öffnen' },
      { key: 'studio.runtime.configure', moduleId: 'platform', label: 'Darstellung konfigurieren' },
    ],
  },
  {
    moduleId: 'admin',
    label: 'Administration',
    permissions: [
      { key: 'admin.view', moduleId: 'admin', label: 'Administration öffnen' },
      { key: 'admin.users.view', moduleId: 'admin', label: 'Benutzer sehen' },
      { key: 'admin.users.manage', moduleId: 'admin', label: 'Benutzer verwalten' },
      { key: 'admin.roles.view', moduleId: 'admin', label: 'Rollen sehen' },
      { key: 'admin.roles.manage', moduleId: 'admin', label: 'Rollen verwalten' },
      { key: 'admin.numbering.view', moduleId: 'admin', label: 'Nummernkreise sehen' },
      { key: 'admin.numbering.manage', moduleId: 'admin', label: 'Nummernkreise verwalten' },
      { key: 'admin.audit.view', moduleId: 'admin', label: 'Audit-Protokoll sehen' },
    ],
  },
  {
    moduleId: 'dashboard',
    label: 'Dashboard',
    permissions: [
      { key: 'dashboard.view', moduleId: 'dashboard', label: 'Dashboard öffnen' },
      { key: 'dashboard.kpis.view', moduleId: 'dashboard', label: 'Kennzahlen sehen' },
      { key: 'dashboard.tasks.view', moduleId: 'dashboard', label: 'Aufgaben sehen' },
    ],
  },
  {
    moduleId: 'customers',
    label: 'Kunden',
    permissions: [
      { key: 'customers.view', moduleId: 'customers', label: 'Kunden sehen' },
      { key: 'customers.create', moduleId: 'customers', label: 'Kunden anlegen' },
      { key: 'customers.edit', moduleId: 'customers', label: 'Kunden bearbeiten' },
    ],
  },
  {
    moduleId: 'machines',
    label: 'Maschinen',
    permissions: [
      { key: 'machines.view', moduleId: 'machines', label: 'Maschinen sehen' },
      { key: 'machines.create', moduleId: 'machines', label: 'Maschinen anlegen' },
      { key: 'machines.edit', moduleId: 'machines', label: 'Maschinen bearbeiten' },
    ],
  },
  {
    moduleId: 'orders',
    label: 'Orders',
    permissions: [
      { key: 'orders.view', moduleId: 'orders', label: 'Vorgänge sehen' },
      { key: 'orders.create', moduleId: 'orders', label: 'Vorgänge anlegen' },
      { key: 'orders.assign', moduleId: 'orders', label: 'Vorgänge zuweisen' },
      { key: 'orders.send_to_billing', moduleId: 'orders', label: 'Zur Abrechnung übergeben' },
    ],
  },
];

function clone<T>(value: T): T {
  return structuredClone(value);
}

function tenantPrefix(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}

function createRoles(tenantId: string): readonly AdminRoleRecord[] {
  const allPermissions = permissionGroups.flatMap((group) => group.permissions.map((item) => item.key));
  return roleTemplates.map(([key, name, description]) => ({
    id: `${tenantId}-${key}`,
    name,
    description,
    isSystem: true,
    userCount: 0,
    permissionKeys: name === 'Administrator'
      ? allPermissions
      : ['platform.access', 'dashboard.view', 'dashboard.tasks.view'],
  }));
}

function withRoleCounts(
  roles: readonly AdminRoleRecord[],
  users: readonly AdminUserRecord[],
): readonly AdminRoleRecord[] {
  return roles.map((role) => ({
    ...role,
    userCount: users.filter((user) => user.active && user.roleIds.includes(role.id)).length,
  }));
}

function createSnapshot(tenantId: string): AdminSnapshot {
  const prefix = tenantPrefix(tenantId);
  const baseRoles = createRoles(tenantId);
  const now = new Date().toISOString();
  const users: readonly AdminUserRecord[] = [
    {
      id: `${tenantId}-frederick`,
      displayName: 'Frederick',
      email: 'frederick@forq.local',
      roleIds: [baseRoles[0].id],
      active: true,
      isCurrentUser: true,
      lastLoginAt: now,
    },
    {
      id: `${tenantId}-sandra`,
      displayName: 'Sandra Beispiel',
      email: 'sandra@forq.local',
      roleIds: [baseRoles[3].id],
      active: true,
      isCurrentUser: false,
      lastLoginAt: '2026-07-12T14:18:00.000Z',
    },
    {
      id: `${tenantId}-markus`,
      displayName: 'Markus Beispiel',
      email: 'markus@forq.local',
      roleIds: [baseRoles[4].id],
      active: true,
      isCurrentUser: false,
      lastLoginAt: '2026-07-12T08:42:00.000Z',
    },
    {
      id: `${tenantId}-tim`,
      displayName: 'Tim Beispiel',
      email: 'tim@forq.local',
      roleIds: [baseRoles[6].id],
      active: false,
      isCurrentUser: false,
      lastLoginAt: null,
    },
  ];

  return {
    tenantId,
    users,
    invitations: [],
    // Zwei Beispielanfragen, damit die Flaeche im Demo-Modus nicht leer aussieht: eine offene und
    // eine bereits entschiedene.
    accessRequests: [
      {
        id: 'req-demo-1',
        email: 'neue.kollegin@foerdertechnik-werl.de',
        displayName: 'Nina Kramer',
        note: 'Neu im Vertriebsinnendienst, brauche Zugriff auf Kunden und Angebote.',
        status: 'pending',
        createdAt: new Date(Date.now() - 3_600_000).toISOString(),
        decidedAt: null,
        decidedByName: null,
        decisionNote: '',
      },
      {
        id: 'req-demo-2',
        email: 'praktikant@foerdertechnik-werl.de',
        displayName: 'Tom Berger',
        note: '',
        status: 'rejected',
        createdAt: new Date(Date.now() - 86_400_000 * 3).toISOString(),
        decidedAt: new Date(Date.now() - 86_400_000 * 2).toISOString(),
        decidedByName: 'Frederick Befeld',
        decisionNote: 'Praktikum erst ab September.',
      },
    ],
    // Ein Auth-User ohne Rolle -- z. B. direkt in Supabase angelegt. Zum Zuordnen im Admin.
    unassignedUsers: [
      {
        id: `${tenantId}-orphan-1`,
        displayName: 'k.befeld',
        email: 'k.befeld@foerdertechnik-werl.de',
        createdAt: new Date(Date.now() - 3_600_000).toISOString(),
        lastLoginAt: null,
      },
    ],
    announcements: demoAnnouncements,
    roles: withRoleCounts(baseRoles, users),
    permissionGroups,
    numberSequences: [
      {
        id: `${tenantId}-customer`, key: 'customer', label: 'Kundennummer', year: null,
        prefix, sequenceLength: 4, nextValue: 1, requiredPermission: 'customers.create', active: true,
      },
      {
        id: `${tenantId}-machine`, key: 'machine', label: 'Erfassungsnummer Maschine', year: null,
        prefix, sequenceLength: 4, nextValue: 1, requiredPermission: 'machines.create', active: true,
      },
      {
        id: `${tenantId}-service-item`, key: 'service_item', label: 'Dienstleistungsnummer', year: null,
        prefix, sequenceLength: 4, nextValue: 1, requiredPermission: 'masterdata.services.create', active: true,
      },
      {
        id: `${tenantId}-business-case`, key: 'business_case', label: 'Vorgangsnummer', year: 2026,
        prefix: '2026', sequenceLength: 5, nextValue: 132, requiredPermission: 'orders.create', active: true,
      },
    ],
    audit: [
      {
        id: `${tenantId}-audit-1`, actor: 'Frederick', action: 'Rolle geprüft',
        entity: 'Administrator', detail: 'Rechtematrix im Demomodus geöffnet', occurredAt: now,
      },
      {
        id: `${tenantId}-audit-2`, actor: 'System', action: 'Nummernkreis initialisiert',
        entity: `${prefix}xxxx`, detail: 'Kunden, Maschinen und Dienstleistungen', occurredAt: '2026-07-13T06:30:00.000Z',
      },
      {
        id: `${tenantId}-audit-3`, actor: 'System', action: 'Mandant aktiviert',
        entity: tenantId.toUpperCase(), detail: 'RLS und Rollenstruktur bereitgestellt', occurredAt: '2026-07-13T06:29:00.000Z',
      },
    ],
    generatedAt: now,
  };
}

/**
 * Die Systemmeldungen des Demo-Modus. Bewusst hier und nicht doppelt in apps/web: das Startfenster
 * im Demo-Modus zeigt GENAU die Meldung, die hier als aktiv markiert ist. Zwei Fixtures wuerden
 * beim ersten Textfix auseinanderlaufen -- und dann zeigt der Demo-Start etwas anderes als das
 * Demo-Adminpanel behauptet.
 */
export const demoAnnouncements: readonly AdminAnnouncementRecord[] = [
  {
    id: 'ann-demo-1',
    title: 'Achtung Beta-Phase!',
    body: 'FORQ ist noch im Aufbau. Bitte meldet Fehler über das Reportingtool — mit Screenshot und dem Vorgang, an dem ihr wart.',
    tone: 'warning',
    repeat: 'every-start',
    active: true,
    updatedAt: '2026-07-17T07:30:00.000Z',
    updatedByName: 'Frederick Befeld',
  },
  {
    id: 'ann-demo-2',
    title: 'Changelog V1.1',
    body: 'Neu: Zugangsanfragen im Adminpanel, Briefbogen je Mandant, E-Rechnung (XRechnung 3.0).',
    tone: 'info',
    repeat: 'once',
    active: false,
    updatedAt: '2026-07-16T09:00:00.000Z',
    updatedByName: 'Frederick Befeld',
  },
];

/** Die aktive Demo-Meldung -- das, was der Demo-Start anzeigt. */
export const activeDemoAnnouncement: SystemAnnouncement | null =
  demoAnnouncements.find((entry) => entry.active) ?? null;

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export class DemoAdminGateway implements AdminGateway {
  private readonly snapshots = new Map<string, AdminSnapshot>();

  private getSnapshot(tenantId: string): AdminSnapshot {
    const existing = this.snapshots.get(tenantId);
    if (existing) return existing;
    const created = createSnapshot(tenantId);
    this.snapshots.set(tenantId, created);
    return created;
  }

  async loadSnapshot(tenantId: string): Promise<AdminSnapshot> {
    return clone(this.getSnapshot(tenantId));
  }

  async inviteUser(input: InviteAdminUserInput): Promise<AdminInvitationOutcome> {
    const current = this.getSnapshot(input.tenantId);
    const email = normalizeEmail(input.email);
    const displayName = input.displayName.trim();
    const roleIds = [...new Set(input.roleIds)];

    if (!email.includes('@') || email.length > 320) throw new Error('Bitte eine gültige E-Mail-Adresse eingeben.');
    if (displayName.length < 2) throw new Error('Bitte einen Namen mit mindestens zwei Zeichen eingeben.');
    if (roleIds.length === 0) throw new Error('Mindestens eine Rolle muss ausgewählt werden.');
    if (roleIds.some((roleId) => !current.roles.some((role) => role.id === roleId))) {
      throw new Error('Eine ausgewählte Rolle gehört nicht zu diesem Mandanten.');
    }
    if (current.users.some((user) => normalizeEmail(user.email) === email)) {
      throw new Error('Diese E-Mail-Adresse besitzt bereits einen Zugang zu diesem Mandanten.');
    }
    if (current.invitations.some((invitation) => normalizeEmail(invitation.email) === email && invitation.status !== 'failed')) {
      throw new Error('Für diese E-Mail-Adresse besteht bereits eine Einladung.');
    }

    const now = new Date().toISOString();
    const suffix = `${Date.now()}-${current.users.length + 1}`;
    const invitation: AdminInvitationRecord = {
      id: `${input.tenantId}-invite-${suffix}`,
      email,
      displayName,
      roleIds,
      status: 'sent',
      errorMessage: null,
      createdAt: now,
    };
    const user: AdminUserRecord = {
      id: `${input.tenantId}-invited-${suffix}`,
      displayName,
      email,
      roleIds,
      active: true,
      isCurrentUser: false,
      lastLoginAt: null,
    };
    const users = [...current.users, user];
    const next: AdminSnapshot = {
      ...current,
      users,
      invitations: [invitation, ...current.invitations],
      roles: withRoleCounts(current.roles, users),
      audit: [{
        id: `${input.tenantId}-audit-invite-${suffix}`,
        actor: 'Frederick',
        action: 'Benutzer eingeladen',
        entity: email,
        detail: 'Demo-Einladung erstellt und Rollen vorgemerkt',
        occurredAt: now,
      }, ...current.audit],
      generatedAt: now,
    };
    this.snapshots.set(input.tenantId, next);
    // Die Demo verschickt nichts und legt kein Konto an -- also gibt es auch
    // kein Anfangspasswort zu zeigen.
    return { snapshot: clone(next), initialPassword: null };
  }

  async setUserActive(tenantId: string, userId: string, active: boolean): Promise<AdminSnapshot> {
    const current = this.getSnapshot(tenantId);
    const target = current.users.find((user) => user.id === userId);
    if (!target) throw new Error('Benutzer wurde in diesem Mandanten nicht gefunden.');
    if (target.isCurrentUser && !active) throw new Error('Das eigene Benutzerkonto kann nicht gesperrt werden.');

    const users = current.users.map((user) => user.id === userId ? { ...user, active } : user);
    const next = {
      ...current,
      users,
      roles: withRoleCounts(current.roles, users),
      generatedAt: new Date().toISOString(),
    };
    this.snapshots.set(tenantId, next);
    return clone(next);
  }

  async setUserRoles(
    tenantId: string,
    userId: string,
    roleIds: readonly string[],
  ): Promise<AdminSnapshot> {
    const current = this.getSnapshot(tenantId);
    const target = current.users.find((user) => user.id === userId);
    if (!target) throw new Error('Benutzer wurde in diesem Mandanten nicht gefunden.');
    if (target.isCurrentUser) throw new Error('Die eigenen Rollen sind geschützt.');
    if (roleIds.length === 0) throw new Error('Mindestens eine Rolle muss zugeordnet bleiben.');
    if (roleIds.some((roleId) => !current.roles.some((role) => role.id === roleId))) {
      throw new Error('Eine ausgewählte Rolle gehört nicht zu diesem Mandanten.');
    }

    const users = current.users.map((user) => (
      user.id === userId ? { ...user, roleIds: [...new Set(roleIds)] } : user
    ));
    const next = {
      ...current,
      users,
      roles: withRoleCounts(current.roles, users),
      generatedAt: new Date().toISOString(),
    };
    this.snapshots.set(tenantId, next);
    return clone(next);
  }

  async assignUser(tenantId: string, userId: string, roleIds: readonly string[]): Promise<AdminSnapshot> {
    const current = this.getSnapshot(tenantId);
    const orphan = current.unassignedUsers.find((entry) => entry.id === userId);
    if (!orphan) throw new Error('Benutzer wurde nicht gefunden oder ist bereits zugeordnet.');
    const uniqueRoles = [...new Set(roleIds)];
    if (uniqueRoles.length === 0) throw new Error('Mindestens eine Rolle muss zugeordnet werden.');
    if (uniqueRoles.some((roleId) => !current.roles.some((role) => role.id === roleId))) {
      throw new Error('Eine ausgewählte Rolle gehört nicht zu diesem Mandanten.');
    }
    const now = new Date().toISOString();
    const user: AdminUserRecord = {
      id: orphan.id, displayName: orphan.displayName, email: orphan.email,
      roleIds: uniqueRoles, active: true, isCurrentUser: false, lastLoginAt: orphan.lastLoginAt,
    };
    const users = [...current.users, user];
    const next: AdminSnapshot = {
      ...current,
      users,
      unassignedUsers: current.unassignedUsers.filter((entry) => entry.id !== userId),
      roles: withRoleCounts(current.roles, users),
      audit: [{
        id: `${tenantId}-audit-assign-${Date.now()}`,
        actor: 'Frederick', action: 'Benutzer zugeordnet', entity: orphan.email,
        detail: 'Bestehender Auth-Benutzer dem Mandanten zugeordnet', occurredAt: now,
      }, ...current.audit],
      generatedAt: now,
    };
    this.snapshots.set(tenantId, next);
    return clone(next);
  }

  async setRolePermission(
    tenantId: string,
    roleId: string,
    permissionKey: string,
    granted: boolean,
  ): Promise<AdminSnapshot> {
    const current = this.getSnapshot(tenantId);
    const next = {
      ...current,
      roles: current.roles.map((role) => {
        if (role.id !== roleId) return role;
        if (role.name === 'Administrator') throw new Error('Die Administratorrolle ist geschützt.');
        const permissions = new Set(role.permissionKeys);
        if (granted) permissions.add(permissionKey);
        else permissions.delete(permissionKey);
        return { ...role, permissionKeys: [...permissions].sort() };
      }),
      generatedAt: new Date().toISOString(),
    };
    this.snapshots.set(tenantId, next);
    return clone(next);
  }

  async setNumberSequenceActive(
    tenantId: string,
    sequenceId: string,
    active: boolean,
  ): Promise<AdminSnapshot> {
    const current = this.getSnapshot(tenantId);
    const next = {
      ...current,
      numberSequences: current.numberSequences.map((sequence) => (
        sequence.id === sequenceId ? { ...sequence, active } : sequence
      )),
      generatedAt: new Date().toISOString(),
    };
    this.snapshots.set(tenantId, next);
    return clone(next);
  }

  async decideAccessRequest(input: DecideAccessRequestInput): Promise<AdminSnapshot> {
    const current = await this.loadSnapshot(input.tenantId);
    const target = current.accessRequests.find((request) => request.id === input.requestId);
    if (!target) throw new Error('Die Anfrage wurde nicht gefunden.');
    if (target.status !== 'pending') throw new Error('Die Anfrage ist bereits entschieden.');

    const next: AdminSnapshot = {
      ...current,
      accessRequests: current.accessRequests.map((request) => request.id === input.requestId
        ? {
            ...request,
            status: input.approve ? 'approved' as const : 'rejected' as const,
            decidedAt: new Date().toISOString(),
            decidedByName: current.users.find((user) => user.isCurrentUser)?.displayName ?? 'Administrator',
            decisionNote: input.note?.trim() ?? '',
          }
        : request),
      generatedAt: new Date().toISOString(),
    };
    this.snapshots.set(input.tenantId, next);
    return clone(next);
  }

  async saveAnnouncement(input: SaveAnnouncementInput): Promise<AdminSnapshot> {
    const current = await this.loadSnapshot(input.tenantId);
    const now = new Date().toISOString();
    const author = current.users.find((user) => user.isCurrentUser)?.displayName ?? 'Administrator';
    const edited: AdminAnnouncementRecord = {
      id: input.id ?? `ann-${current.announcements.length + 1}-${now}`,
      title: input.title.trim(),
      body: input.body.trim(),
      tone: input.tone,
      repeat: input.repeat,
      active: input.active,
      updatedAt: now,
      updatedByName: author,
    };

    if (input.id && !current.announcements.some((entry) => entry.id === input.id)) {
      throw new Error('Die Systemmeldung wurde nicht gefunden.');
    }

    // Wie admin_save_announcement: Aktivieren verdraengt die bisher aktive Meldung, statt eine
    // zweite danebenzustellen. Sonst laufen Demo-Modus und Datenbank auseinander.
    const others = current.announcements
      .filter((entry) => entry.id !== input.id)
      .map((entry) => (input.active ? { ...entry, active: false } : entry));

    const next: AdminSnapshot = {
      ...current,
      announcements: [edited, ...others],
      generatedAt: now,
    };
    this.snapshots.set(input.tenantId, next);
    return clone(next);
  }

  async deleteAnnouncement(tenantId: string, announcementId: string): Promise<AdminSnapshot> {
    const current = await this.loadSnapshot(tenantId);
    const next: AdminSnapshot = {
      ...current,
      announcements: current.announcements.filter((entry) => entry.id !== announcementId),
      generatedAt: new Date().toISOString(),
    };
    this.snapshots.set(tenantId, next);
    return clone(next);
  }
}

export function createDemoAdminGateway(): AdminGateway {
  return new DemoAdminGateway();
}
